import React, { useState, useCallback } from 'react';
import {
    Box, Paper, Divider, Stack, Typography, TextField, Button, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import Swal from 'sweetalert2';
import { fetchConToken } from 'helpers/fetch';
import { getDataSession } from 'views/Utils/Common';
import Modal from 'components/modal/Modal';
import { useModal } from 'hooks/useModal';
import { currency } from 'resources/utilities';

const mapDetallesSurtir = (venta) => (venta.venta_Detalles || []).map((detalle) => {
    const pendiente = (detalle.unidades_X_Surtir ?? 0) - (detalle.unidades_Surtidas ?? 0);
    return {
        id: detalle.id,
        clave_Articulo: detalle.clave_Articulo,
        nombre_Articulo: detalle.nombre_Articulo,
        cantidad: detalle.cantidad,
        unidades_X_Surtir: detalle.unidades_X_Surtir ?? 0,
        pendiente,
        unidadesSurtir: pendiente > 0 ? pendiente : 0
    };
});

const procesarRespuestaRemision = (resp, onSuccess) => {
    if (resp?.hasOwnProperty('status') && resp.status === 400) {
        Object.entries(resp.errors).forEach(([, value]) => Swal.fire('Error', value[0], 'error'));
        return false;
    }
    if (resp?.hasOwnProperty('codigo') && resp.codigo !== 200) {
        Swal.fire('Error', resp.mensajes[0], 'error');
        return false;
    }
    Swal.fire('Exito', 'Se Convirtio a Remision', 'success');
    if (onSuccess) onSuccess();
    return true;
};

const SurtirRemisionModal = ({
    venta,
    detallesSurtir,
    onSurtirChange,
    onConfirmar,
    onCancelar,
    totalUnidadesSurtir,
    clienteNombre,
    clienteRfc,
}) => (
    <Box sx={{ minWidth: { xs: '100%', md: 720 } }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 3, py: 2, borderRadius: '4px 4px 0 0', mx: -1, mt: -1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Inventory2Icon />
                <Typography variant="h6" component="h2">Surtido parcial — Convertir a Remisión</Typography>
            </Stack>
        </Box>

        <Box sx={{ px: 1, pt: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <PersonIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>Información del pedido</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Cliente</Typography>
                        <Typography variant="body1" fontWeight={500}>{venta?.nombre_Cliente || clienteNombre || 'Sin cliente'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">RFC</Typography>
                        <Typography variant="body1">{venta?.rfc_Cliente || clienteRfc || '—'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Folio / Orden</Typography>
                        <Typography variant="body1">{venta?.folio} · {venta?.no_Orden || '—'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Importe total</Typography>
                        <Typography variant="body1" fontWeight={500}>{currency(venta?.importe_Total_Impuestos ?? 0)}</Typography>
                    </Box>
                </Box>
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><strong>Clave</strong></TableCell>
                            <TableCell><strong>Artículo</strong></TableCell>
                            <TableCell align="center"><strong>Cantidad</strong></TableCell>
                            <TableCell align="center"><strong>Pendiente</strong></TableCell>
                            <TableCell align="center" sx={{ minWidth: 130 }}><strong>Surtido</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detallesSurtir?.map((detalle) => (
                            <TableRow key={detalle.id} hover sx={detalle.pendiente <= 0 ? { opacity: 0.55 } : undefined}>
                                <TableCell>{detalle.clave_Articulo}</TableCell>
                                <TableCell>{detalle.nombre_Articulo}</TableCell>
                                <TableCell align="center">{detalle.cantidad}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={detalle.pendiente}
                                        size="small"
                                        color={detalle.pendiente > 0 ? 'warning' : 'default'}
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <TextField
                                        type="number"
                                        size="small"
                                        inputProps={{ min: 0, max: detalle.pendiente, step: 'any', style: { textAlign: 'center' } }}
                                        value={detalle.unidadesSurtir}
                                        disabled={detalle.pendiente <= 0}
                                        onChange={({ target }) => onSurtirChange(detalle.id, target.value)}
                                        sx={{ width: 100 }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    Total a surtir: <strong>{totalUnidadesSurtir}</strong> unidad(es)
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" color="inherit" onClick={onCancelar}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" onClick={onConfirmar} disabled={totalUnidadesSurtir <= 0}>
                        Surtir y Convertir
                    </Button>
                </Stack>
            </Stack>
        </Box>
    </Box>
);

const useSurtirRemision = ({ onSuccess, clienteNombre, clienteRfc } = {}) => {
    const [isOpenModal, openModal, closeModal] = useModal(false);
    const [ventaSurtido, setVentaSurtido] = useState(null);
    const [detallesSurtir, setDetallesSurtir] = useState([]);

    const cerrarModalSurtido = useCallback(() => {
        closeModal();
        setVentaSurtido(null);
        setDetallesSurtir([]);
    }, [closeModal]);

    const abrirModalSurtidoParcial = useCallback((venta) => {
        setVentaSurtido(venta);
        setDetallesSurtir(mapDetallesSurtir(venta));
        openModal();
    }, [openModal]);

    const ejecutarSurtidoTotal = useCallback((venta_id, sucursal_Id) => {
        Swal.fire({
            title: 'Surtir y convertir',
            text: '¿Desea surtir el pedido completo y convertirlo a remisión?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, surtir y convertir',
            cancelButtonText: 'No'
        }).then((result) => {
            if (!result.isConfirmed) return;

            const payload = {
                documento_Id: venta_id,
                sucursal_Id: sucursal_Id ?? getDataSession('sucursal'),
                tomarComprometidos: false,
                surtido_Total: true,
                detalles: []
            };

            fetchConToken('Pedidos/ConvertirARemision', payload, 'post').then((resp) => {
                procesarRespuestaRemision(resp, onSuccess);
            }).catch((error) => console.error('Error al convertir a remision:', error));
        });
    }, [onSuccess]);

    const convertirRemision = useCallback((venta_id) => {
        Swal.fire({
            title: 'Tipo de surtido',
            html: `
                <p style="margin:0 0 12px">¿Cómo desea surtir este pedido?</p>
                <p style="margin:0;font-size:13px;color:#666;text-align:left">
                    <strong>Surtido total:</strong> convierte todas las unidades pendientes en remisión.<br/>
                    <strong>Surtido parcial:</strong> elige la cantidad a surtir por artículo.
                </p>
            `,
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Surtido total',
            denyButtonText: 'Surtido parcial',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1976d2',
            denyButtonColor: '#ed6c02',
        }).then((result) => {
            if (result.isConfirmed) {
                ejecutarSurtidoTotal(venta_id, getDataSession('sucursal'));
                return;
            }
            if (!result.isDenied) return;

            fetchConToken(`Ventas?id=${venta_id}&sucursal_Id=${getDataSession('sucursal')}`).then((venta) => {
                if (venta?.hasOwnProperty('status') && venta.status === 400) {
                    Object.entries(venta.errors).forEach(([, value]) => Swal.fire('Error', value[0], 'error'));
                    return;
                }
                if (venta?.hasOwnProperty('codigo') && venta.codigo !== 200) {
                    Swal.fire('Error', venta.mensajes[0], 'error');
                    return;
                }
                abrirModalSurtidoParcial(venta);
            }).catch((error) => console.error('Error al obtener la venta:', error));
        });
    }, [abrirModalSurtidoParcial, ejecutarSurtidoTotal]);

    const handleSurtirChange = useCallback((detalleId, value) => {
        setDetallesSurtir((prev) => prev.map((detalle) => {
            if (detalle.id !== detalleId) return detalle;

            let unidadesSurtir = parseFloat(value);
            if (isNaN(unidadesSurtir) || unidadesSurtir < 0) unidadesSurtir = 0;
            if (unidadesSurtir > detalle.pendiente) unidadesSurtir = detalle.pendiente;

            return { ...detalle, unidadesSurtir };
        }));
    }, []);

    const confirmarSurtirRemision = useCallback(() => {
        const detallesEnviar = detallesSurtir.filter(x => x.unidadesSurtir > 0);
        if (detallesEnviar.length === 0) {
            Swal.fire('Info', 'Debe indicar al menos una unidad a surtir', 'info');
            return;
        }

        const totalUnidades = detallesEnviar.reduce((acc, d) => acc + d.unidadesSurtir, 0);

        Swal.fire({
            title: 'Surtir y convertir',
            text: `¿Desea surtir ${totalUnidades} unidad(es) y convertir el pedido a remisión?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, surtir y convertir',
            cancelButtonText: 'No'
        }).then((result) => {
            if (!result.isConfirmed) return;

            const payload = {
                documento_Id: ventaSurtido.id,
                sucursal_Id: ventaSurtido.sucursal_Id ?? getDataSession('sucursal'),
                tomarComprometidos: false,
                surtido_Total: false,
                detalles: detallesEnviar.map(x => ({
                    detalle_Venta_Id: x.id,
                    unidades_Surtir: x.unidadesSurtir
                }))
            };

            fetchConToken('Pedidos/ConvertirARemision', payload, 'post').then((resp) => {
                procesarRespuestaRemision(resp, () => {
                    cerrarModalSurtido();
                    onSuccess?.();
                });
            }).catch((error) => console.error('Error al convertir a remision:', error));
        });
    }, [detallesSurtir, ventaSurtido, cerrarModalSurtido, onSuccess]);

    const totalUnidadesSurtir = detallesSurtir.reduce((acc, d) => acc + (d.unidadesSurtir || 0), 0);

    const SurtirRemisionPortal = () => (
        <Modal isOpen={isOpenModal} closeModal={cerrarModalSurtido} min>
            <SurtirRemisionModal
                venta={ventaSurtido}
                detallesSurtir={detallesSurtir}
                onSurtirChange={handleSurtirChange}
                onConfirmar={confirmarSurtirRemision}
                onCancelar={cerrarModalSurtido}
                totalUnidadesSurtir={totalUnidadesSurtir}
                clienteNombre={clienteNombre}
                clienteRfc={clienteRfc}
            />
        </Modal>
    );

    return { convertirRemision, SurtirRemisionPortal };
};

export { useSurtirRemision };
